import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { formatDate } from "../../../helpers/UtilityHelper";
import ProzoDataGrid from "../../../common/ProzoDataGrid";
import MUIModal from "../../../common/MUIModal";
import { apiConstants, postGeneric } from "../../../common/constants";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import Loader from "../../../common/Loader"
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, SUCCESS, ERROR } from '../../../styles/foundation/colors';
import { dataGridWrapSx } from '../../../styles/patterns';
import { primaryTextSx, secondaryTextSx, cellStackSx } from '../list2/cellStyles';

export default function BulkOrderResults(props) {
    const { shipmentType, reload, resultType } = props;
    const [data, setData] = useState([])
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [seeOrders, setSeeOrders] = useState(false)
    const [orderData, setOrderData] = useState([])
    // Modal has its OWN pagination state so paging inside it stays client-side and
    // never re-triggers the outer list's API call (which caused the modal to flicker).
    const [modalPage, setModalPage] = useState(0)
    const [modalSizePerPage, setModalSizePerPage] = useState(10)
    const [fn, setFn] = useState('proship');
    const [isLoading, setIsLoading] = useState(false)
    const merchant = getMerchant();

    useEffect(() => {
        setIsLoading(true)
        let type = resultType || (shipmentType === "B2B" ? "B2B_ORDER" : "ORDER");
        const request = {
            "collection": "bulk_orders_result",
            "offset": (page) * sizePerPage,
            "limit": sizePerPage,
            "andfilter": [
                { "field": "type", "operator": "eq", "value": type },
                { field: "merchantId", operator: "eq", value: merchant?.merchantId }
            ],
            "sort": { "field": "time", "direction": "DESC" }
        }
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, request).then((res) => {
            setData(res?.data?.data)
        }).finally(() => setIsLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizePerPage, page, reload, shipmentType, resultType])

    const columnsOrderModal = [
        { field: "reference", headerAlign: "left", hideSortIcons: true, headerName: "Reference Number", width: 200 },
        { field: "awb", headerAlign: "left", hideSortIcons: true, headerName: "AWB", width: 200 },
        {
            field: "reason", headerAlign: "left", hideSortIcons: true, headerName: "Message", width: 500,
            renderCell: ({ row }) => (
                <Typography sx={{ ...secondaryTextSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row?.reason || ''}>
                    {row?.reason || '—'}
                </Typography>
            ),
        },
    ];

    const columns = [
        {
            field: 'file', headerAlign: "left", hideSortIcons: true, headerName: 'File Details', flex: 1.5, minWidth: 180,
            renderCell: (params) => {
                const row = params.row;
                return (
                    <Box sx={cellStackSx}>
                        <Typography sx={primaryTextSx}>{row?.fileName}</Typography>
                        <Typography sx={secondaryTextSx}>{row?.modifiedByUser}</Typography>
                        <Typography sx={secondaryTextSx}>{row?.time ? formatDate(row.time) : ''}</Typography>
                    </Box>
                )
            },
        },
        {
            field: 'status', headerAlign: "left", hideSortIcons: true, headerName: 'Status', flex: 0.8, minWidth: 110,
            renderCell: (params) => {
                const isCompleted = params.row?.status === 'COMPLETED';
                const color = isCompleted ? SUCCESS : ERROR;
                return (
                    <Box sx={cellStackSx}>
                        <Chip size="small" variant="outlined" label={isCompleted ? 'Completed' : 'Stuck / Failed'}
                            sx={{
                                fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: 11, height: 24,
                                bgcolor: color + '18', color, border: `1px solid ${color}40`,
                                '& .MuiChip-label': { px: 1 },
                            }} />
                    </Box>
                )
            },
        },
        {
            field: 'type', headerAlign: "left", hideSortIcons: true, headerName: 'Type', flex: 0.5, minWidth: 70,
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={secondaryTextSx}>{params.row?.type}</Typography>
                </Box>
            ),
        },
        {
            field: 'totalOrders', headerAlign: "left", hideSortIcons: true, headerName: 'Total', flex: 0.5, minWidth: 60,
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={primaryTextSx}>{params.row?.totalOrders}</Typography>
                </Box>
            ),
        },
        {
            field: 'processed', headerAlign: "left", hideSortIcons: true, headerName: 'Processed', flex: 0.5, minWidth: 80,
            renderCell: (params) => (
                <Box sx={cellStackSx}>
                    <Typography sx={primaryTextSx}>{params.row?.processed}</Typography>
                </Box>
            ),
        },
        {
            field: 'successOrders', headerAlign: "left", hideSortIcons: true, headerName: 'Successful', flex: 0.7, minWidth: 100,
            renderCell: (params) => {
                const row = params.row;
                if (row?.successOrders?.length > 0) {
                    return (
                        <Box sx={cellStackSx}>
                            <Button size="small" sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: SUCCESS, whiteSpace: 'nowrap' }}
                                onClick={() => { setOrderData(row.successOrders.map((o, i) => ({ ...o, __rowId: i }))); setModalPage(0); setSeeOrders(true); setFn(row.fileName ? row.fileName + '_success' : 'proship11') }}>
                                View ({row.successOrders.length})
                            </Button>
                        </Box>
                    )
                }
                return <Typography sx={secondaryTextSx}>—</Typography>
            },
        },
        {
            field: 'failedOrders', headerAlign: "left", hideSortIcons: true, headerName: 'Failed', flex: 0.7, minWidth: 100,
            renderCell: (params) => {
                const row = params.row;
                if (row?.failedOrders?.length > 0) {
                    return (
                        <Box sx={cellStackSx}>
                            <Button size="small" sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], textTransform: 'none', color: ERROR, whiteSpace: 'nowrap' }}
                                onClick={() => { setOrderData(row.failedOrders.map((o, i) => ({ ...o, __rowId: i }))); setModalPage(0); setSeeOrders(true); setFn(row.fileName ? row.fileName + '_failed' : 'proship') }}>
                                View ({row.failedOrders.length})
                            </Button>
                        </Box>
                    )
                }
                return <Typography sx={secondaryTextSx}>—</Typography>
            },
        },
    ]

    return (
        <>
            {isLoading && <Loader />}
            <Box sx={dataGridWrapSx}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
                        Upload History
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.3 }}>
                        Track the status and results of your bulk upload jobs
                    </Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                    <ProzoDataGrid
                        columns={columns}
                        rows={data ? data : []}
                        sizePerPage={sizePerPage}
                        setSizePerPage={setSizePerPage}
                        setPage={setPage}
                        page={page}
                        pagination={true}
                        hideFooterPagination={false}
                        filterMode={"server"}
                        rowCount={Number.MAX_VALUE}
                        hideCountNumber={true}
                        getRowId={(row) => row._id}
                    />
                </Box>
            </Box>

            <MUIModal open={seeOrders} title="Upload Details" maxWidth={"lg"} handleClose={() => setSeeOrders(false)}>
                <ProzoDataGrid
                    rowHeight={36}
                    sizePerPage={modalSizePerPage}
                    setSizePerPage={setModalSizePerPage}
                    page={modalPage}
                    setPage={setModalPage}
                    exportToExcel={true}
                    exportFileName={fn}
                    rowCount={orderData?.length}
                    columns={columnsOrderModal}
                    rows={orderData}
                    paginationMode={'client'}
                    pagination={true}
                    hideFooterPagination={false}
                    getRowId={(row) => row.__rowId}
                />
            </MUIModal>
        </>
    )
}
